/**
 * PHASE 0 (hybrid pivot) — optional native-app bridge context.
 *
 * The native Expo app (artifacts/mobile) embeds selected web screens —
 * starting with checkout — inside Expo DOM components (WebViews). For
 * those embeds, web Stripe Elements is the wrong payment surface: the
 * native app already ships @stripe/stripe-react-native's Payment Sheet
 * (Apple Pay / Google Pay / native 3DS). This context is the seam.
 *
 * Contract:
 *   - On the REAL website this context is never provided. `useNativeBridge()`
 *     returns null and every consumer MUST behave exactly as before —
 *     providing the bridge is the ONLY way to change behavior.
 *   - When the mobile embedding kit (artifacts/mobile/dom/kit) provides it,
 *     the checkout pay action short-circuits Stripe Elements: it still runs
 *     the normal `prepareBooking` (server creates trip + PaymentIntent and
 *     returns the clientSecret) and then hands the clientSecret to
 *     `requestNativePayment`, which presents the NATIVE payment sheet and
 *     resolves with the outcome.
 *
 * Consumers (additive hooks, see components/checkout/InlinePayment.tsx):
 *   - `InlinePayButton` renders a bridge-backed pay button instead of the
 *     Stripe-Elements confirm flow.
 *   - `PaymentElementsWrapper` skips mounting Stripe Elements entirely
 *     (no Stripe.js inside the WebView).
 *   - `InlinePaymentSection` renders a "you'll pay in the secure native
 *     payment sheet" panel instead of PaymentElement / ExpressCheckout.
 */

import { createContext, useContext, type ReactNode } from "react";

export interface NativePaymentRequest {
  /** PaymentIntent (`pi_..._secret_...`) or SetupIntent (`seti_...`)
   *  client secret minted by prepareBooking on the server. */
  clientSecret: string;
  /** Regional publishable key from the quote (multi-region Stripe).
   *  Optional — the native side falls back to its configured default. */
  publishableKey?: string;
  /** Stripe region slug from the quote (e.g. "uk", "us"), informational. */
  region?: string;
  /** Selects PaymentIntent versus SetupIntent confirmation on the native side. */
  intentType?: "payment" | "setup";
}

export interface NativePaymentResult {
  ok: boolean;
  /** True when the user dismissed the native sheet without paying. */
  canceled?: boolean;
  /** Human-readable failure reason when ok === false. */
  message?: string;
}

export interface NativeAuthRequest {
  intent?: "signin" | "signup" | "auto";
  /** Web path the user was heading to (informational; the native app
   *  resumes its own navigation after sign-in). */
  next?: string;
}

export interface NativeBridge {
  /** Present the native payment sheet for this intent. Must resolve
   *  (never reject) — wrap native errors into `{ ok:false, message }`. */
  requestNativePayment: (
    req: NativePaymentRequest,
  ) => Promise<NativePaymentResult>;
  /** PHASE 1 — open the native sign-in / sign-up screen (embedded web
   *  screens have no auth modal). Optional: hosts that embed only
   *  authenticated screens may omit it. */
  requestAuth?: (req?: NativeAuthRequest) => void | Promise<void>;
}

const NativeBridgeContext = createContext<NativeBridge | null>(null);

export function NativeBridgeProvider({
  bridge,
  children,
}: {
  bridge: NativeBridge;
  children: ReactNode;
}) {
  return (
    <NativeBridgeContext.Provider value={bridge}>
      {children}
    </NativeBridgeContext.Provider>
  );
}

/**
 * Null on the real website (no provider mounted). Consumers must treat
 * null as "no bridge" and keep their existing behavior bit-for-bit.
 */
export function useNativeBridge(): NativeBridge | null {
  return useContext(NativeBridgeContext);
}
